import { openDocument } from './documentNavigation';
import {
  getEvalSetViewUrl,
  getLogViewUrl,
  getSampleViewUrl,
  type SampleViewAnchor,
} from './url';

export type ViewerLevel = 'eval_set' | 'log' | 'sample';

export type ViewerUrlTemplates = Record<ViewerLevel, string | null>;

export interface ConfiguredViewer {
  id: string;
  name: string;
  kind: 'same_origin' | 'external';
  url_templates: ViewerUrlTemplates;
}

export interface BuiltinViewer {
  id: typeof BUILTIN_VIEWER_ID;
  name: string;
  kind: 'builtin';
}

export type Viewer = BuiltinViewer | ConfiguredViewer;

export const BUILTIN_VIEWER_ID = 'hawk';

export const BUILTIN_VIEWER: BuiltinViewer = {
  id: BUILTIN_VIEWER_ID,
  name: 'Inspect (built-in)',
  kind: 'builtin',
};

export type ViewerTarget =
  | { level: 'eval_set'; evalSetId: string }
  | { level: 'log'; evalSetId: string; logFile: string }
  | {
      level: 'sample';
      evalSetId: string;
      logFile: string;
      sampleId: string;
      epoch: number;
      sampleUuid?: string | null;
      /** Built-in viewer only. */
      anchor?: SampleViewAnchor;
    };

const LEVELS: ViewerLevel[] = ['eval_set', 'log', 'sample'];

const SCHEME_PATTERN = /^([a-zA-Z][a-zA-Z0-9+.-]*):/;
const HOST_PLACEHOLDER_PATTERN = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\/[^/?#]*\{/;
const BLOCKED_SCHEMES = new Set([
  'javascript',
  'data',
  'vbscript',
  'blob',
  'file',
]);

type TemplatePart = { literal: string } | { placeholder: string };

/** Splits a `str.format`-style template; null if malformed. */
function parseTemplate(template: string): TemplatePart[] | null {
  const parts: TemplatePart[] = [];
  let literal = '';
  let i = 0;
  while (i < template.length) {
    const c = template[i];
    if (c === '{' || c === '}') {
      if (template[i + 1] === c) {
        literal += c;
        i += 2;
        continue;
      }
      if (c === '}') return null;
      const end = template.indexOf('}', i + 1);
      if (end === -1) return null;
      const name = template.slice(i + 1, end);
      if (!/^[a-z_]+$/.test(name)) return null;
      if (literal) parts.push({ literal });
      literal = '';
      parts.push({ placeholder: name });
      i = end + 1;
      continue;
    }
    literal += c;
    i += 1;
  }
  if (literal) parts.push({ literal });
  return parts;
}

function parseViewer(raw: unknown): ConfiguredViewer | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const { id, name, kind, url_templates } = raw as Record<string, unknown>;
  if (typeof id !== 'string' || id === BUILTIN_VIEWER_ID) return null;
  if (typeof name !== 'string') return null;
  if (kind !== 'same_origin' && kind !== 'external') return null;
  const rawTemplates = (url_templates ?? {}) as Record<string, unknown>;
  const templates = Object.fromEntries(
    LEVELS.map(level => {
      const template = rawTemplates[level];
      const usable =
        typeof template === 'string' &&
        template !== '' &&
        !HOST_PLACEHOLDER_PATTERN.test(template);
      return [level, usable ? template : null];
    })
  ) as ViewerUrlTemplates;
  return { id, name, kind, url_templates: templates };
}

export function parseViewers(raw: unknown): ConfiguredViewer[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap(entry => parseViewer(entry) ?? []);
}

/** Percent-encodes each `/`-separated segment; null if any is `.` or `..`. */
function encodeSegments(value: string): string | null {
  const segments = value.split('/');
  if (segments.some(segment => segment === '.' || segment === '..')) {
    return null;
  }
  return segments.map(encodeURIComponent).join('/');
}

function placeholderValues(target: ViewerTarget): Record<string, string> {
  const values: Record<string, string> = { eval_set_id: target.evalSetId };
  if (target.level === 'eval_set') return values;
  values.log_file = target.logFile;
  values.log_path = `${target.evalSetId}/${target.logFile}`;
  if (target.level === 'log') return values;
  values.sample_id = target.sampleId;
  values.epoch = String(target.epoch);
  if (target.sampleUuid) values.sample_uuid = target.sampleUuid;
  return values;
}

function resolveBuiltinUrl(target: ViewerTarget): string {
  switch (target.level) {
    case 'eval_set':
      return getEvalSetViewUrl(target.evalSetId);
    case 'log':
      return getLogViewUrl({
        evalSetId: target.evalSetId,
        filename: target.logFile,
      });
    case 'sample':
      return getSampleViewUrl({
        evalSetId: target.evalSetId,
        filename: target.logFile,
        sampleId: target.sampleId,
        epoch: target.epoch,
        anchor: target.anchor,
      });
  }
}

function isAllowedUrl(url: string, viewer: ConfiguredViewer): boolean {
  if (viewer.kind === 'external') {
    const scheme = SCHEME_PATTERN.exec(url)?.[1].toLowerCase();
    return scheme !== undefined && !BLOCKED_SCHEMES.has(scheme);
  }
  try {
    const parsed = new URL(url, window.location.origin);
    return (
      parsed.origin === window.location.origin &&
      parsed.pathname.startsWith(`/${viewer.id}/`)
    );
  } catch {
    return false;
  }
}

export function resolveViewerUrl(
  viewer: Viewer,
  target: ViewerTarget
): string | null {
  if (viewer.kind === 'builtin') return resolveBuiltinUrl(target);
  const template = viewer.url_templates[target.level];
  const parts = template === null ? null : parseTemplate(template);
  if (!parts) return null;
  const values = placeholderValues(target);
  let url = '';
  for (const part of parts) {
    if ('literal' in part) {
      url += part.literal;
      continue;
    }
    const value = values[part.placeholder];
    const encoded = value === undefined ? null : encodeSegments(value);
    if (encoded === null) return null;
    url += encoded;
  }
  return isAllowedUrl(url, viewer) ? url : null;
}

export interface ViewerOption {
  viewer: Viewer;
  /** Null when the viewer can't open this target. */
  url: string | null;
  isDefault: boolean;
}

export function viewerOptions(
  viewers: Viewer[],
  defaultViewerId: string,
  target: ViewerTarget
): ViewerOption[] {
  return viewers.map(viewer => ({
    viewer,
    url: resolveViewerUrl(viewer, target),
    isDefault: viewer.id === defaultViewerId,
  }));
}

type ViewerUrlKind = 'internal' | 'web' | 'app';

export function viewerUrlKind(url: string): ViewerUrlKind {
  const scheme = SCHEME_PATTERN.exec(url)?.[1].toLowerCase();
  if (scheme === undefined) return 'internal';
  if (scheme !== 'http' && scheme !== 'https') return 'app';
  try {
    return new URL(url).origin === window.location.origin ? 'internal' : 'web';
  } catch {
    return 'web';
  }
}

/**
 * The default viewer's URL for `target`, else the built-in one, which alone
 * honours sample anchors. With `redirect` (navigation without a click) a
 * native app scheme is never used.
 */
export function primaryViewerUrl(
  viewers: Viewer[],
  defaultViewerId: string,
  target: ViewerTarget,
  { redirect = false }: { redirect?: boolean } = {}
): string {
  const anchored = target.level === 'sample' && target.anchor !== undefined;
  const viewer = anchored
    ? undefined
    : viewers.find(v => v.id === defaultViewerId);
  const url = viewer ? resolveViewerUrl(viewer, target) : null;
  if (url !== null && !(redirect && viewerUrlKind(url) === 'app')) return url;
  return resolveBuiltinUrl(target);
}

/** Anchor `target`/`rel`: other web hosts open in a new tab. */
export function viewerLinkTarget(url: string): {
  target?: '_blank';
  rel?: string;
} {
  if (viewerUrlKind(url) === 'web') {
    return { target: '_blank', rel: 'noopener noreferrer' };
  }
  return {};
}

/** Opens a viewer URL from a non-link click, e.g. a grid row. */
export function openViewerUrl(
  url: string,
  { newTab = false }: { newTab?: boolean } = {}
): void {
  const kind = viewerUrlKind(url);
  if (kind === 'web') {
    window.open(url, '_blank', 'noopener,noreferrer');
  } else if (kind === 'internal' && newTab) {
    window.open(url, '_blank');
  } else {
    openDocument(url);
  }
}
